import { EventEmitter, Input, OnInit, Output, Directive } from '@angular/core';
import { BaseWithBundleComponent } from './base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { BaseHttpService, UriBuilderParams } from '@app/core/services/base/base-http.service';
import { BaseWithId } from '@app/shared/models/base';
import { Params } from '@angular/router';
import { DrI18nService } from '@app/core/services/i18n/i18n.service';
import { DrQueryParams } from '@app/core/services/base/base-service-with-bundle.service';
import { Observable } from 'rxjs';

export interface MightBeGetter<T extends BaseWithId, U extends UriBuilderParams<T>> {
    getById?(uriParams: U, params?: DrQueryParams, propOverride?: string): Observable<T>;
}

export interface BPCConfig {
    fetchAtInit: boolean;
}

@Directive()
export abstract class BaseChangeComponent<T extends BaseWithId, U extends UriBuilderParams<T>> extends BaseWithBundleComponent
                                                                                               implements OnInit {

    protected myConfig: BPCConfig = { fetchAtInit: false }; // override in sub class if object of T needs to be fetched during component init.
    protected patchParams: DrQueryParams;

    @Input()
    set bananaBox(objectOfT: T) {
        this.myConfig.fetchAtInit = false;
        this.reInitOriginalAndUpdated(objectOfT);
    }

    @Output()
    public bananaBoxChange = new EventEmitter<T>();

    get id(): string {
        return this.myUriIdsList && this.myUriIdsList.length > 0 && this.myUriIdsList[0];
    }

    protected abstract paramsIdNames: string[];
    protected myUriIdsList: string[];

    public original: T;
    public updated: T;


    protected constructor(protected componentServiceBundle: ComponentServiceBundle,
                          protected drHttpService: BaseHttpService<T, U> & MightBeGetter<T, U>) {
        super(componentServiceBundle);
    }

    protected abstract generateUriBuilderParams(): U;

    public ngOnInit() {
        super.ngOnInit();
        this.getOriginal();
    }

    public getOriginal(): void {
        this.myUriIdsList = new Array<string>();
        this.route.firstChild.params.subscribe((params: Params) => {
            if (this.paramsIdNames) {
                for (let i = 0; i < this.paramsIdNames.length; i++) {
                    const id = params[this.paramsIdNames[i]];
                    this.myUriIdsList.push(id);
                }
            }

            const uriBuilderParams = this.generateUriBuilderParams();
            if (uriBuilderParams && this.myConfig.fetchAtInit && typeof this.drHttpService.getById === 'function') {
                this.drHttpService.getById(this.generateUriBuilderParams(), this.patchParams)
                    .subscribe((result: T) => {
                            this.original = result;
                            this.updated = this.initNew(result);
                            this.whenOriginalFetched();
                            this.buildBreadcrumb();
                        },
                        err => this.handleError(err)
                    );
            } else {
                this.whenOriginalFetched();
                this.buildBreadcrumb();
            }
        });
    }

    // Override when additional fetches need to be performed based on the values of the original object
    protected whenOriginalFetched(): void {}

    // Override when the new objects need to be initialized via a different way than the constructorT (example via some sort of factory method)
    protected initNew(result: T): T {
        const constructorX = this.drHttpService.getConstructor(this.drHttpService.constructorT, result);
        return new constructorX(result);
    }

    protected reInitOriginalAndUpdated(result: T): void {
        this.original = this.initNew(result);
        this.updated = this.initNew(result);
    }

    protected afterChange(result: T, callbackFn?: Function): void {
        this.reInitOriginalAndUpdated(result);

        if (callbackFn) {
            callbackFn(result);
        }

        this.bananaBoxChange.emit(this.original);
    }

    public dateTimeToDateString(date: Date): string | null {
        return DrI18nService.dateTimeToDateOnlyString(date);
    }
}
